import { useEffect, useState } from 'react';
import { useDebounce } from './useDebounce';
import { OrderParams, View } from '../models/Calculate';
import { findViewValuesHelper, getErrorMessage } from '../utils';
import { paymentsApi } from '../services/apiServices/user/payments';
import { useCustomSnackbar } from './useCustomEnqueueSnackbar';
import { defaultCurrentPrice } from '../models/DefaultState';

export interface CurrentPrice {
  amount: number;
  balance: number;
  bonus: number;
  totalAmount: number;
  backAmount: number;
  bonusAmount: number;
  backBonusAmount: number;
  discountAmount: number;
  haveAmount: number;
  haveBonusAmount: number;
  leftAmount: number;
  leftBonusAmount: number;
  payAmount: number;
  payBonusAmount: number;
  monthCount: number;
  fromDate: string;
  toDate: string;
  discountPercent: number;
  canBalanceAmount: number;
  earnBonusPercent: number;
  earnBonusAmount: number;
  view: View;
}

export const useCalculate = (params?: OrderParams, isSkip?: boolean) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInitialDiscount50Percent, setIsInitialDiscount50Percent] = useState(false);
  const [currentCalculation, setCurrentCalculation] = useState<CurrentPrice>(defaultCurrentPrice);
  const [calculateCostAPI] = paymentsApi.useCalculateCostMutation();
  const { customEnqueueSnackbar } = useCustomSnackbar();

  const calculateService = useDebounce(async (arg: OrderParams) => {
    try {
      await calculateCostAPI(arg)
        .unwrap()
        .then((resp) => {
          setCurrentCalculation({
            amount: resp.amount,
            totalAmount: resp.total_amount,
            backAmount: resp.back_amount,
            bonusAmount: resp.bonus_amount,
            backBonusAmount: resp.back_bonus_amount,
            discountAmount: resp.discount_amount,
            haveAmount: resp.have_amount,
            haveBonusAmount: resp.have_bonus_amount,
            leftAmount: resp.left_amount,
            leftBonusAmount: resp.left_bonus_amount,
            payAmount: resp.pay_amount,
            payBonusAmount: resp.pay_bonus_amount,
            canBalanceAmount: resp.can_balance_amount,
            earnBonusPercent: resp.earn_bonus_percent,
            earnBonusAmount: resp.earn_bonus_amount,
            monthCount: params?.count || 1,
            view: resp.view,
            balance: resp.balance,
            bonus: resp.bonus,
            fromDate: `${new Date(+findViewValuesHelper(resp.view, 'fts') * 1000)}`,
            toDate: `${new Date(+findViewValuesHelper(resp.view, 'tts') * 1000)}`,
            discountPercent: +findViewValuesHelper(resp.view, 'dsc_percent') || 0,
          });
          setIsLoading(false);
          if (!isLoaded) {
            setIsInitialDiscount50Percent(+findViewValuesHelper(resp.view, 'dsc_percent') === 50);
            setIsLoaded(true);
          }
        });
    } catch (err) {
      const message = getErrorMessage(err);
      setIsLoading(false);
      customEnqueueSnackbar(message, 'error');
    }
  }, 300);

  useEffect(() => {
    if (!isSkip) {
      setIsLoading(true);
      calculateService(params);
    } else {
      setIsLoading(false);
    }
  }, [params]);

  return { isLoading, isLoaded, isInitialDiscount50Percent, currentCalculation };
};
